import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Download, FileText, AlertTriangle } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { getImportedBookings, getOtherIncome, type ImportedBooking, type OtherIncome } from '../api';
import { formatDate } from '../utils/dates';
import { PageHeader, Button } from '../components/ui';

// ── Måned-vælger helpers ────────────────────────────────────────────────────

const DA_MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];

function startOfPrevMonth(): { year: number; month: number } {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function recentMonths(n: number): Array<{ year: number; month: number }> {
  const out: Array<{ year: number; month: number }> = [];
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  for (let i = 0; i < n; i++) {
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

// ── Rapport-niveauer ────────────────────────────────────────────────────────

type Level = 'standard' | 'detaljeret' | 'fuld';

const LEVEL_LABELS: Record<Level, string> = {
  standard:   'Standard',
  detaljeret: 'Detaljeret',
  fuld:       'Fuld',
};

// ── Kolonne-definitioner ────────────────────────────────────────────────────

interface Col {
  label: string;
  kind: 'reg' | 'renter' | 'date_start' | 'date_end' | 'num';
  /** For 'num': returnerer celleværdi for én booking */
  value: (b: ImportedBooking) => number;
  /** Hvilke niveauer kolonnen vises i */
  levels: Level[];
  /** Bredde i PDF (mm). Auto hvis udeladt */
  pdfWidth?: number;
  /** Vægtet stil i tabel */
  emphasis?: 'rental' | 'payout' | 'diff';
}

// Helper til at læse felter sikkert som tal
const N = (b: ImportedBooking, k: keyof ImportedBooking) => Number(b[k] ?? 0);

// Konsolideret leje for STANDARD — alt der bidrog til indtjeningen (minus selvrisiko + gebyr).
// extras_total er fakturaens frie linjer (fx "Afhentning af bil i Randers"). De lå kun i
// rfo_booking_extras og manglede derfor helt i rapporten, så manuelle fakturaer blev vist
// for lavt — både i omsætning og i salgsmoms.
const stdLeje = (b: ImportedBooking): number =>
  N(b, 'rental_amount') + N(b, 'dynamic_pricing')
  + N(b, 'km_extra_amount') + N(b, 'km_overage_amount')
  + N(b, 'fuel_adjustment') + N(b, 'late_return_fee')
  + N(b, 'extra_driver_fee') + N(b, 'foreign_driving_fee')
  + N(b, 'extras_total')
  - N(b, 'discount');

// Konsolideret leje for DETALJERET — kerne-leje (uden tillægs-gebyrer og fakturalinjer)
const detLeje = (b: ImportedBooking): number =>
  N(b, 'rental_amount') + N(b, 'dynamic_pricing') - N(b, 'discount');

// Alt hvad lejeren er faktureret, inkl. momsfri selvrisiko, ekskl. gebyr
const invoiceRevenue = (b: ImportedBooking): number => stdLeje(b) + N(b, 'excess_reduction');

// Faktureret = det beløb der forventes ind på kontoen.
// GoMore udfylder payout (leje minus deres gebyr), men kender kun sine egne poster. Egne krav
// lagt på bookingen bagefter — parkeringsafgift, skadeserstatning — står i extras_total og skal
// lægges til, ellers optræder de i omsætningen uden nogensinde at blive et udestående.
// Manuelle fakturaer har payout = NULL; dér indeholder invoiceRevenue allerede fakturalinjerne.
const faktureret = (b: ImportedBooking): number =>
  b.payout != null
    ? Number(b.payout) + N(b, 'extras_total')
    : invoiceRevenue(b) - N(b, 'handling_fees');

// Modtaget = summen af betalingsposteringer matchet til bookingen, uanset posteringsdato.
// En forudbetaling i april på en faktura dateret i juni tæller altså med i juni — kolonnen
// svarer på "er denne udlejning betalt", ikke "hvad landede der i denne måned".
const modtaget = (b: ImportedBooking): number => N(b, 'received_total');

const differens = (b: ImportedBooking): number => modtaget(b) - faktureret(b);

const COLUMNS: Col[] = [
  // Identifikation — lejernavn er irrelevant for regnskab (dokumentation findes i selve booking-detalje siden)
  { label: 'Bil',    kind: 'reg',        value: () => 0, levels: ['standard', 'detaljeret', 'fuld'], pdfWidth: 22 },
  { label: 'Start',  kind: 'date_start', value: () => 0, levels: ['standard', 'detaljeret', 'fuld'], pdfWidth: 26 },
  { label: 'Slut',   kind: 'date_end',   value: () => 0, levels: ['standard', 'detaljeret', 'fuld'], pdfWidth: 26 },

  // Leje — forskellige formler pr niveau
  { label: 'Leje',      kind: 'num', value: stdLeje, levels: ['standard'],   emphasis: 'rental' },
  { label: 'Leje',      kind: 'num', value: detLeje, levels: ['detaljeret'], emphasis: 'rental' },
  { label: 'Lejebeløb', kind: 'num', value: b => N(b, 'rental_amount'),   levels: ['fuld'], emphasis: 'rental' },
  { label: 'Dynamic',   kind: 'num', value: b => N(b, 'dynamic_pricing'), levels: ['fuld'] },
  { label: 'Rabat',     kind: 'num', value: b => -N(b, 'discount'),       levels: ['fuld'] },  // vises negativ

  // Km-beløb (i Detaljeret + Fuld)
  { label: 'Km ekstra',   kind: 'num', value: b => N(b, 'km_extra_amount'),   levels: ['detaljeret', 'fuld'] },
  { label: 'Km overskr.', kind: 'num', value: b => N(b, 'km_overage_amount'), levels: ['detaljeret', 'fuld'] },

  // Tillægs-gebyrer (i Detaljeret + Fuld)
  { label: 'Brændstof',      kind: 'num', value: b => N(b, 'fuel_adjustment'),     levels: ['detaljeret', 'fuld'] },
  { label: 'Sen aflevering', kind: 'num', value: b => N(b, 'late_return_fee'),     levels: ['detaljeret', 'fuld'] },
  { label: 'Ekstra fører',   kind: 'num', value: b => N(b, 'extra_driver_fee'),    levels: ['detaljeret', 'fuld'] },
  { label: 'Udland',         kind: 'num', value: b => N(b, 'foreign_driving_fee'), levels: ['detaljeret', 'fuld'] },
  { label: 'Tillæg',         kind: 'num', value: b => N(b, 'extras_total'),        levels: ['detaljeret', 'fuld'] },

  // Altid
  { label: 'Selvrisiko', kind: 'num', value: b => N(b, 'excess_reduction'), levels: ['standard', 'detaljeret', 'fuld'] },
  { label: 'Gebyr',      kind: 'num', value: b => N(b, 'handling_fees'),    levels: ['standard', 'detaljeret', 'fuld'] },
  { label: 'Faktureret', kind: 'num', value: faktureret, levels: ['standard', 'detaljeret', 'fuld'], emphasis: 'payout' },
  { label: 'Modtaget',   kind: 'num', value: modtaget,   levels: ['standard', 'detaljeret', 'fuld'] },
  { label: 'Difference', kind: 'num', value: differens,  levels: ['standard', 'detaljeret', 'fuld'], emphasis: 'diff' },
];

// En række hører til rapporten hvis den enten er faktureret eller bærer rigtige beløb.
// Udlejninger uden fakturanummer blev tidligere kasseret tavst — det kostede juni 2026
// en udlejning på 1.060,54 kr, hvor GoMore havde afregnet pengene uden nogensinde at
// generere en faktura. Andre gange er fakturaen bare ikke udstedt endnu ved sidste
// synkronisering. I begge tilfælde er omsætningen reel: beløbene skal med, og det
// manglende fakturanummer er en oplysning frem for en grund til at udelade rækken.
const hasAmounts = (b: ImportedBooking): boolean =>
  invoiceRevenue(b) !== 0 || N(b, 'payout') !== 0 || N(b, 'received_total') !== 0;

// ── Komponent ───────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const fmt = (n: number) => formatNumber(n, settings.numberFormat);

  const initial = startOfPrevMonth();
  const [year, setYear]   = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [level, setLevel] = useState<Level>('standard');

  const [allBookings, setAllBookings] = useState<ImportedBooking[]>([]);
  const [loading, setLoading]         = useState(true);

  const [otherIncome, setOtherIncome] = useState<OtherIncome[]>([]);

  useEffect(() => {
    getImportedBookings().then(b => {
      setAllBookings(b);
      setLoading(false);
    });
    // Aflysningsgebyrer hører til udlejninger der aldrig blev til noget, så de har ingen booking
    // at stå på. Fejler kaldet, mangler kun den ene linje — resten af rapporten skal stadig virke.
    getOtherIncome().then(setOtherIncome).catch(() => setOtherIncome([]));
  }, []);

  const monthBookings = useMemo(() => {
    const yyyy = String(year);
    const mm = String(month).padStart(2, '0');
    return allBookings.filter(b => {
      if (b.invoice_number == null && !hasAmounts(b)) return false;
      const dateStr = b.invoice_date ?? b.rental_start ?? '';
      return dateStr.startsWith(yyyy) && dateStr.slice(5, 7) === mm;
    }).sort((a, b) => (a.invoice_date ?? a.rental_start ?? '').localeCompare(b.invoice_date ?? b.rental_start ?? ''));
  }, [allBookings, year, month]);

  // Rækker der mangler fakturanummer, og rækker hvor det modtagne ikke matcher det fakturerede.
  // Begge dele vises som advarsel i stedet for at ligge skjult i tallene.
  const missingInvoiceNo = useMemo(
    () => monthBookings.filter(b => b.invoice_number == null),
    [monthBookings]
  );
  const unbalanced = useMemo(
    () => monthBookings.filter(b => Math.abs(differens(b)) >= 1),
    [monthBookings]
  );
  // Rækker hvor rapportens eget regnestykke ikke går op: Leje + selvrisiko − Gebyr skal give
  // Faktureret. Gør det ikke det, mangler eller fejler et af tallene på selve bookingen — sådan
  // opdagede vi at gebyret stod til 249 hvor fakturaen sagde 275,71, og at Sofus' udlejning slet
  // ikke havde noget gebyr registreret. 2 øre svarer til GoMores egen afrunding.
  const inkonsistente = useMemo(
    () => monthBookings.filter(b => Math.abs(invoiceRevenue(b) - N(b, 'handling_fees') - faktureret(b)) >= 0.02),
    [monthBookings]
  );

  const visibleCols = useMemo(() => COLUMNS.filter(c => c.levels.includes(level)), [level]);

  // Totaler — for hver synlig num-kolonne
  const totalsByCol = useMemo(() => {
    const map = new Map<Col, number>();
    for (const col of visibleCols) {
      if (col.kind !== 'num') continue;
      let sum = 0;
      for (const b of monthBookings) sum += col.value(b);
      map.set(col, sum);
    }
    return map;
  }, [visibleCols, monthBookings]);

  // Moms-overslag (bruger alle felter uanset rapport-niveau)
  const vatRate = settings.vatRate;
  // Kun den momspligtige del af fakturalinjerne tælles med — linjer som "mistet lejeindtægt"
  // eller skadeerstatning markeres momsfri ved oprettelsen.
  const salesVat = useMemo(() => monthBookings.reduce((s, i) => {
    const taxable = N(i, 'rental_amount') + N(i, 'dynamic_pricing')
                  + N(i, 'km_extra_amount') + N(i, 'km_overage_amount')
                  + N(i, 'fuel_adjustment') + N(i, 'late_return_fee')
                  + N(i, 'extra_driver_fee') + N(i, 'foreign_driving_fee')
                  + N(i, 'extras_vat_total')
                  - N(i, 'discount');
    return s + taxable * vatRate / (100 + vatRate);
  }, 0), [monthBookings, vatRate]);
  const totalInvoiced = useMemo(
    () => monthBookings.reduce((s, b) => s + faktureret(b), 0),
    [monthBookings]
  );
  const totalReceived = useMemo(
    () => monthBookings.reduce((s, b) => s + modtaget(b), 0),
    [monthBookings]
  );
  const totalHandling = useMemo(
    () => monthBookings.reduce((s, b) => s + N(b, 'handling_fees'), 0),
    [monthBookings]
  );
  // purchaseVat defineres nedenfor — den skal først kende månedens aflysninger, fordi et gebyr
  // VI har betalt bærer købsmoms der er fradragsberettiget på linje med GoMores handling fees.

  // Aflysningsgebyrer i måneden. Datoen er posteringsdatoen — altså hvornår pengene kom ind.
  // De er momspligtige: et gebyr knyttet til en momspligtig ydelse bærer moms med, og GoMores
  // kvittering anfører selv 25%. Derfor indgår de i salgsmoms-grundlaget nedenfor.
  const monthOtherIncome = useMemo(() => {
    const mm = `${year}-${String(month).padStart(2, '0')}`;
    return otherIncome.filter(o => String(o.posting_date).startsWith(mm));
  }, [otherIncome, year, month]);
  const otherIncomeTotal = useMemo(
    () => monthOtherIncome.reduce((s, o) => s + Number(o.amount), 0),
    [monthOtherIncome]
  );
  // Momsen på aflysningsgebyrer BEREGNES — den læses ikke fra kvitteringen (besluttet 2026-08-19).
  //
  // GoMore undlader at anføre moms på kunder uden for EU. Det er forkert: bookingen er indgået på
  // dansk jord, så gebyret er momspligtigt her uanset kundens hjemland. Dertil ligger vores
  // gebyrstørrelser lige på grænsen af hvor momspligten indtræder — afhængigt af hvornår der
  // aflyses opkræves 25% eller 40% af lejen, og kun den lave sats kunne tænkes at gå fri.
  // Beløbene er små, og risikoen ved at afregne for lidt er større end omkostningen ved at
  // afregne for meget. Derfor moms på alle, uanset hvad bilaget siger.
  //
  // rfo_cancellations.vat_amount bevares som dokumentation for HVAD GOMORE SKREV. Den er
  // bevidst ikke overskrevet: afviger den fra det vi bogfører, er det en oplysning værd at have.
  // Købsmoms (egne aflysninger) hører til i den anden rubrik og holdes udenfor her.
  //
  // Uden importeret kvittering kender vi ikke RETNINGEN, og den kan ikke udledes af fortegnet:
  // et negativt beløb er enten et gebyr VI betalte (købsmoms) eller en tilbageførsel af et gebyr
  // vi opkrævede (negativ salgsmoms). Anders' -75 den 08-12-2025 er det første, Mats' -500 den
  // 28-07-2025 det andet — samme fortegn, hver sin rubrik. Derfor gættes der ikke: posteringen
  // holdes ude af momssummen og tælles i advarslen, indtil kvitteringen er hentet.
  const otherIncomeVat = useMemo(
    () => monthOtherIncome.reduce(
      (s, o) => (o.invoice_number == null || o.vat_type === 'koebsmoms')
        ? s
        : s + Number(o.amount) * vatRate / (100 + vatRate),
      0
    ),
    [monthOtherIncome, vatRate]
  );
  // En aflysning uden importeret kvittering mangler sit bilag OG sin momsrubrik. Revisoren skal
  // kunne se det. invoice_number kommer fra rfo_cancellations og er kun udfyldt når den er hentet.
  const otherIncomeUdenBilag = useMemo(
    () => monthOtherIncome.filter(o => o.invoice_number == null),
    [monthOtherIncome]
  );
  // KØBSMOMS fra egne aflysninger. Aflyser vi selv, betaler vi GoMore et gebyr, og kvitteringen
  // anfører købsmoms ("Gebyr betalt til GoMore … Inkluderer købsmoms (25%)"). Den er
  // fradragsberettiget på linje med handling fees — uden den her forsvandt fradraget helt:
  // posteringen holdes ude af salgsmomsen, men blev aldrig lagt til i den anden rubrik.
  //
  // Til FORSKEL fra salgsmomsen beregnes den ikke, men læses fra bilaget. Et fradrag kræver
  // dokumentation, så mangler kvitteringen, kræves der intet. Salgsmoms må gerne afregnes for
  // meget; købsmoms må ikke fratrækkes for meget.
  //
  // vat_amount gemmes med samme fortegn som gebyret (negativt = penge ud), mens purchaseVat er
  // et positivt fradragsbeløb — derfor abs().
  const otherIncomePurchaseVat = useMemo(
    () => monthOtherIncome.reduce(
      (s, o) => (o.vat_type === 'koebsmoms' && o.invoice_number != null && o.vat_amount != null)
        ? s + Math.abs(Number(o.vat_amount))
        : s,
      0
    ),
    [monthOtherIncome]
  );
  const purchaseVat = totalHandling * vatRate / (100 + vatRate) + otherIncomePurchaseVat;
  const totalSalesVat = salesVat + otherIncomeVat;

  const monthLabel = `${DA_MONTHS[month - 1]} ${year}`;
  const monthOptions = recentMonths(24);

  // ── Cell-tekst ───────────────────────────────────────────────────────────
  // pdf=true sætter * på biler uden fakturanummer, så markeringen også overlever i PDF'en
  // hvor vi ikke kan style enkeltceller med farve.
  const cellText = (b: ImportedBooking, col: Col, pdf = false): string => {
    switch (col.kind) {
      case 'reg':        return (b.registration_number ?? '—') + (pdf && b.invoice_number == null ? ' *' : '');
      case 'renter':     return b.renter_name ?? '—';
      case 'date_start': return b.rental_start ? formatDate(b.rental_start) : '—';
      case 'date_end':   return b.rental_end   ? formatDate(b.rental_end)   : '—';
      case 'num':        return fmt(col.value(b));
    }
  };

  // ── PDF-export ────────────────────────────────────────────────────────────
  const downloadPdf = () => {
    // Standard = A4 portrait, Detaljeret + Fuld = A4 landscape med lodrette talkolonne-headers
    const isLandscape = level !== 'standard';
    const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Udlejningsrapport (${LEVEL_LABELS[level].toLowerCase()})`, 14, 16);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(monthLabel, 14, 22);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Genereret ${new Date().toLocaleDateString('da-DK')} for ${user?.name ?? ''}`, 14, 27);
    doc.setTextColor(0);

    doc.setFontSize(9);
    doc.text(`${monthBookings.length} udlejninger`, pageWidth - 14, 16, { align: 'right' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Faktureret: ${fmt(totalInvoiced)} DKK`, pageWidth - 14, 22, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Modtaget: ${fmt(totalReceived)} · Salgsmoms: ${fmt(totalSalesVat)} · Købsmoms: ${fmt(purchaseVat)}`,
      pageWidth - 14, 27, { align: 'right' });

    const columnStyles: Record<number, { halign?: 'right'; cellWidth?: number; fontStyle?: 'bold' }> = {};
    visibleCols.forEach((col, i) => {
      const style: { halign?: 'right'; cellWidth?: number; fontStyle?: 'bold' } = {};
      if (col.kind === 'num') style.halign = 'right';
      if (col.pdfWidth) style.cellWidth = col.pdfWidth;
      if (col.emphasis === 'payout') style.fontStyle = 'bold';
      columnStyles[i] = style;
    });

    const rotateNumHeaders = isLandscape;

    autoTable(doc, {
      startY: 33,
      head: [visibleCols.map(c => c.label)],
      body: monthBookings.map(b => visibleCols.map(c => cellText(b, c, true))),
      foot: [visibleCols.map(c => {
        if (c.kind === 'num') return fmt(totalsByCol.get(c) ?? 0);
        if (c === visibleCols[0]) return `Total (${monthBookings.length})`;
        return '';
      })],
      showFoot: 'lastPage',
      styles: { fontSize: isLandscape ? 8 : 9, cellPadding: 2 },
      headStyles: {
        fillColor: [236, 233, 226], textColor: 50, fontStyle: 'bold',
        ...(rotateNumHeaders ? { minCellHeight: 28, valign: 'bottom' } : {}),
      },
      footStyles: { fillColor: [242, 240, 235], textColor: 0, fontStyle: 'bold' },
      columnStyles,
      margin: { bottom: 18 },
      didParseCell: (data) => {
        const col = visibleCols[data.column.index];
        // Differencer der ikke går op markeres rødt — ellers drukner de i en kolonne af nuller
        if (data.section === 'body' && col?.emphasis === 'diff') {
          const b = monthBookings[data.row.index];
          if (b && Math.abs(differens(b)) >= 1) data.cell.styles.textColor = [190, 40, 40];
        }
        if (!rotateNumHeaders || data.section !== 'head') return;
        if (col?.kind === 'num') data.cell.text = [''];
      },
      didDrawCell: (data) => {
        if (!rotateNumHeaders || data.section !== 'head') return;
        const col = visibleCols[data.column.index];
        if (col?.kind === 'num') {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50);
          const x = data.cell.x + data.cell.width / 2 + 1.2;
          const y = data.cell.y + data.cell.height - 2;
          doc.text(col.label, x, y, { angle: 90 });
          doc.setTextColor(0);
        }
      },
    });

    // Noter under tabellen — advarslerne skal med i PDF'en, ikke kun på skærmen.
    // Uden dem kan revisoren ikke se at et tal er usikkert.
    // @ts-expect-error — lastAutoTable sættes på doc af jspdf-autotable
    let noteY = (doc.lastAutoTable?.finalY ?? 33) + 6;
    if (otherIncomeTotal !== 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(
        `Aflysningsgebyrer (${monthOtherIncome.length}): ${fmt(otherIncomeTotal)} DKK — heraf moms ${fmt(otherIncomeVat)} DKK (beregnet ${vatRate}%).`
        + (otherIncomeUdenBilag.length > 0 ? `  ${otherIncomeUdenBilag.length} uden importeret kvittering.` : '')
        + `  Indtægt i alt: ${fmt(totalInvoiced + otherIncomeTotal)} DKK`,
        14, noteY, { maxWidth: pageWidth - 28 }
      );
      doc.setFont('helvetica', 'normal');
      noteY += 8;
    }

    if (missingInvoiceNo.length > 0 || unbalanced.length > 0 || inkonsistente.length > 0) {
      let y = noteY;
      doc.setFontSize(8);
      if (missingInvoiceNo.length > 0) {
        doc.setTextColor(150, 90, 20);
        doc.text(
          `* ${missingInvoiceNo.length} udlejning(er) uden fakturanummer, i alt ${fmt(missingInvoiceNo.reduce((s, b) => s + faktureret(b), 0))} DKK. `
          + 'Beløbene er med i totalerne. Fakturaen er enten ikke udstedt endnu ved sidste synkronisering, eller slet ikke genereret af GoMore.',
          14, y, { maxWidth: pageWidth - 28 }
        );
        y += 8;
      }
      if (unbalanced.length > 0) {
        doc.setTextColor(190, 40, 40);
        doc.text(
          `${unbalanced.length} udlejning(er) hvor modtaget beløb afviger fra faktureret, i alt ${fmt(unbalanced.reduce((s, b) => s + differens(b), 0))} DKK.`,
          14, y, { maxWidth: pageWidth - 28 }
        );
        y += 8;
      }
      if (inkonsistente.length > 0) {
        doc.setTextColor(190, 40, 40);
        doc.text(
          `${inkonsistente.length} udlejning(er) hvor Leje + Selvrisiko − Gebyr ikke giver Faktureret, i alt `
          + `${fmt(inkonsistente.reduce((s, b) => s + invoiceRevenue(b) - N(b, 'handling_fees') - faktureret(b), 0))} DKK. `
          + 'Typisk et gebyr der mangler på bookingen.',
          14, y, { maxWidth: pageWidth - 28 }
        );
      }
      doc.setTextColor(0);
    }

    // Sidetal
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Side ${i} af ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
      doc.setTextColor(0);
    }

    doc.save(`udlejningsrapport-${level}-${year}-${String(month).padStart(2, '0')}.pdf`);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.admin_group')}
        title={t('nav.reports')}
        subtitle="Månedlig udlejningsrapport"
        actions={
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Download size={14} />}
            onClick={downloadPdf}
            disabled={monthBookings.length === 0}
          >
            Download PDF
          </Button>
        }
      />

      {/* Måned-vælger + niveau-vælger */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-500 font-medium">Måned:</span>
          <select
            className="filter-input"
            value={`${year}-${String(month).padStart(2, '0')}`}
            onChange={e => {
              const [y, m] = e.target.value.split('-').map(Number);
              setYear(y); setMonth(m);
            }}
          >
            {monthOptions.map(o => (
              <option key={`${o.year}-${o.month}`} value={`${o.year}-${String(o.month).padStart(2, '0')}`}>
                {DA_MONTHS[o.month - 1]} {o.year}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-500 font-medium">Niveau:</span>
          <div className="flex gap-1">
            {(['standard', 'detaljeret', 'fuld'] as Level[]).map(l => (
              <button key={l} onClick={() => setLevel(l)}
                className={`btn-toggle ${level === l ? 'btn-toggle-active' : 'btn-toggle-inactive'}`}>
                {LEVEL_LABELS[l]}
              </button>
            ))}
          </div>
        </div>
        <span className="text-sm text-ink-500 ml-auto">
          {monthBookings.length} udlejninger · Faktureret <span className="font-semibold text-ink-900">{fmt(totalInvoiced)} DKK</span>
          {' · '}Modtaget <span className="font-semibold text-ink-900">{fmt(totalReceived)} DKK</span>
        </span>
      </div>

      {/* Advarsler — rækker der mangler fakturanummer eller ikke er betalt fuldt ud.
          De tælles med i totalerne, men skal være synlige så et tal aldrig kan være
          forkert uden at nogen kan se det. */}
      {!loading && (missingInvoiceNo.length > 0 || unbalanced.length > 0 || inkonsistente.length > 0) && (
        <div className="mb-4 space-y-2">
          {inkonsistente.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{inkonsistente.length}</strong> {inkonsistente.length === 1 ? 'udlejning hvor' : 'udlejninger hvor'} rapportens eget regnestykke ikke går op
                {' '}— Leje + Selvrisiko − Gebyr giver ikke Faktureret. Afvigelse i alt {fmt(inkonsistente.reduce((s, b) => s + invoiceRevenue(b) - N(b, 'handling_fees') - faktureret(b), 0))} DKK.
                {' '}Typisk et manglende gebyr på bookingen, fx fordi GoMore endnu ikke har udstedt fakturaen.
                {' '}Berørte biler: {[...new Set(inkonsistente.map(b => b.registration_number ?? '—'))].join(', ')}.
              </span>
            </div>
          )}
          {missingInvoiceNo.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{missingInvoiceNo.length}</strong> {missingInvoiceNo.length === 1 ? 'udlejning mangler' : 'udlejninger mangler'} fakturanummer
                {' '}({fmt(missingInvoiceNo.reduce((s, b) => s + faktureret(b), 0))} DKK) — markeret med <span className="font-mono">!</span> i tabellen.
                Beløbene er med i totalerne. Fakturaen er enten ikke udstedt endnu ved sidste synkronisering, eller slet ikke genereret af GoMore.
              </span>
            </div>
          )}
          {unbalanced.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{unbalanced.length}</strong> {unbalanced.length === 1 ? 'udlejning hvor' : 'udlejninger hvor'} det modtagne beløb afviger fra det fakturerede
                {' '}(i alt {fmt(unbalanced.reduce((s, b) => s + differens(b), 0))} DKK). Se kolonnen Difference.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Tabel */}
      <div className="list-card">
        {loading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : monthBookings.length === 0 ? (
          <div className="p-12 text-center text-ink-500">
            <FileText size={32} className="mx-auto text-ink-300 mb-3" />
            <p>Ingen udlejninger i {monthLabel}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`data-table text-xs ${level !== 'standard' ? 'min-w-[1200px]' : ''}`}>
              <thead>
                <tr>
                  {visibleCols.map((c, i) => (
                    <th key={i} className={`th ${c.kind === 'num' ? '!text-right' : ''}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthBookings.map(b => {
                  const noInvoiceNo = b.invoice_number == null;
                  const off = Math.abs(differens(b)) >= 1;
                  return (
                    <tr key={b.id} className="row-hover">
                      {visibleCols.map((c, i) => {
                        const isNum = c.kind === 'num';
                        const isReg = c.kind === 'reg';
                        const isRenter = c.kind === 'renter';
                        let cls = 'px-3 py-2 ';
                        if (isReg) cls += 'font-mono text-ink-900 whitespace-nowrap';
                        else if (isRenter) cls += 'text-ink-700 truncate max-w-[200px]';
                        else if (isNum) {
                          cls += 'text-right font-mono ';
                          if (c.emphasis === 'diff') cls += off ? 'font-semibold text-rose-700' : 'text-ink-400';
                          else if (c.emphasis === 'payout') cls += 'font-semibold text-ink-900';
                          else if (c.emphasis === 'rental') cls += 'text-ink-900';
                          else cls += 'text-ink-500';
                        } else cls += 'text-ink-500 whitespace-nowrap';
                        return (
                          <td key={i} className={cls}
                            title={isRenter ? (b.renter_name ?? '') : undefined}>
                            {cellText(b, c)}
                            {isReg && noInvoiceNo && (
                              <span className="ml-1 font-sans font-bold text-amber-600"
                                title="Fakturanummer mangler — beløbet er med i totalerne">!</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="text-xs font-medium text-ink-700">
                <tr>
                  {(() => {
                    // Find indeks for første num-kolonne — alt før det collapses i én "Total (n)"-celle
                    const firstNumIdx = visibleCols.findIndex(c => c.kind === 'num');
                    const colSpan = firstNumIdx > 0 ? firstNumIdx : 1;
                    return (
                      <>
                        <td colSpan={colSpan} className="px-3 py-2">Total ({monthBookings.length})</td>
                        {visibleCols.slice(firstNumIdx).map((c, i) => {
                          if (c.kind !== 'num') return <td key={`spacer-${i}`} className="px-3 py-2" />;
                          const v = totalsByCol.get(c) ?? 0;
                          let cls = 'px-3 py-2 text-right font-mono';
                          if (c.emphasis === 'diff') cls += Math.abs(v) >= 1 ? ' font-semibold text-rose-700' : '';
                          else if (c.emphasis === 'payout') cls += ' font-semibold text-ink-900';
                          return <td key={`tot-${i}`} className={cls}>{fmt(v)}</td>;
                        })}
                      </>
                    );
                  })()}
                </tr>
                {otherIncomeTotal !== 0 && (
                  <tr>
                    <td colSpan={visibleCols.length} className="px-3 pt-2 pb-1 text-ink-700 font-normal border-t border-ink-100">
                      Aflysningsgebyrer ({monthOtherIncome.length}): <span className="text-ink-900 font-mono font-semibold">{fmt(otherIncomeTotal)}</span>
                      <span className="text-ink-400"> — heraf moms {fmt(otherIncomeVat)} (beregnet {vatRate}%)</span>
                      {otherIncomeUdenBilag.length > 0 && (
                        <span className="text-amber-700"> ({otherIncomeUdenBilag.length} uden importeret kvittering)</span>
                      )}
                      {' · '}
                      Indtægt i alt: <span className="text-ink-900 font-mono font-semibold">{fmt(totalInvoiced + otherIncomeTotal)}</span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={visibleCols.length} className="px-3 pb-3 text-ink-500 font-normal">
                    Salgsmoms (25/125 af momspligtige indtægter): <span className="text-ink-900 font-mono">{fmt(totalSalesVat)}</span>
                    {' · '}
                    Købsmoms (GoMore-gebyrer{otherIncomePurchaseVat !== 0 ? ' + egne aflysninger' : ''}): <span className="text-ink-900 font-mono">{fmt(purchaseVat)}</span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
